// Flight tapes: FPV moments as raw data, not video. A tape is the camera
// track + stick positions + the audio-reactivity features, sampled at 30 Hz —
// enough to RE-RENDER the flight in any browser at full quality, at a few
// tens of kilobytes instead of megabytes of video.
//
// Wire format (deflated, then base64url for #flight= links / raw for .flight
// files):  "FVT1" | u32 headerLen | header JSON | i16 pos ×3n (×200, ~5 mm)
//          | i16 quat ×4n (×32767) | i8 sticks ×4n (×127) | u8 audio ×5n (×255)
// Pos + quat are per-channel DELTA-coded before deflate — smooth flight data
// makes the deltas tiny, which roughly halves the link size. Int16 wraparound
// is modular, so encode−decode is lossless even if a delta ever overflowed.
// Quaternions renormalise on decode. Pure module — unit-tested headless.

import { deflateSync, inflateSync } from "fflate";

export const TAPE_HZ = 30;
export const TAPE_MAX_SEC = 60;
export const TAPE_MAX_N = TAPE_HZ * TAPE_MAX_SEC;

export type TapeMeta = {
  fisheye: number;   // lens setting at record time (drives replay FOV + distortion)
  ground: boolean;   // grass floor on during the flight
  groundAlt: number; // metres
  groundDark: number; // grass darkness at record time (0 bright → 1 out)
};

export type FlightTape = {
  v: 1;
  hz: number;
  n: number;              // sample count
  pos: Float32Array;      // n*3 camera position, scene units
  quat: Float32Array;     // n*4 camera quaternion (x,y,z,w)
  sticks: Float32Array;   // n*4 throttle 0..1, yaw/pitch/roll -1..1
  audio: Float32Array;    // n*5 level/bass/mid/treble/beat 0..1
  meta: TapeMeta;
};

const MAGIC = "FVT1";
const POS_Q = 200;    // 1/200 scene unit ≈ 6 mm
const QUAT_Q = 32767;
const STICK_Q = 127;

const clamp = (v: number, a: number, b: number) => Math.min(Math.max(v, a), b);

/** Quantise, then per-channel delta-code in place (stride = channels per sample). */
function quantDelta(src: Float32Array, count: number, scale: number, lim: number, stride: number): Int16Array {
  const q = new Int16Array(count);
  for (let i = 0; i < count; i++) q[i] = Math.round(clamp(src[i], -lim, lim) * scale);
  for (let i = count - 1; i >= stride; i--) q[i] = q[i] - q[i - stride]; // Int16Array wraps → modular, lossless
  return q;
}

export function encodeTape(t: FlightTape): Uint8Array {
  const header = new TextEncoder().encode(JSON.stringify({ v: t.v, hz: t.hz, n: t.n, meta: t.meta }));
  const total = 8 + header.length + t.n * (3 * 2 + 4 * 2 + 4 + 5);
  const raw = new Uint8Array(total);
  const dv = new DataView(raw.buffer);
  for (let i = 0; i < 4; i++) raw[i] = MAGIC.charCodeAt(i);
  dv.setUint32(4, header.length, true);
  raw.set(header, 8);
  const qpos = quantDelta(t.pos, t.n * 3, POS_Q, 163, 3);
  const qquat = quantDelta(t.quat, t.n * 4, QUAT_Q, 1, 4);
  let o = 8 + header.length;
  for (let i = 0; i < t.n * 3; i++, o += 2) dv.setInt16(o, qpos[i], true);
  for (let i = 0; i < t.n * 4; i++, o += 2) dv.setInt16(o, qquat[i], true);
  for (let i = 0; i < t.n * 4; i++, o += 1) dv.setInt8(o, Math.round(clamp(t.sticks[i], -1, 1) * STICK_Q));
  for (let i = 0; i < t.n * 5; i++, o += 1) raw[o] = Math.round(clamp(t.audio[i], 0, 1) * 255);
  return deflateSync(raw, { level: 9 });
}

export function decodeTape(bytes: Uint8Array): FlightTape {
  const raw = inflateSync(bytes);
  const dv = new DataView(raw.buffer, raw.byteOffset, raw.byteLength);
  if (String.fromCharCode(raw[0], raw[1], raw[2], raw[3]) !== MAGIC) throw new Error("not a flight tape");
  const hLen = dv.getUint32(4, true);
  const h = JSON.parse(new TextDecoder().decode(raw.subarray(8, 8 + hLen))) as
    { v: number; hz: number; n: number; meta: TapeMeta };
  if (h.v !== 1 || !(h.n > 0) || !(h.hz > 0)) throw new Error("unsupported tape version");
  const n = Math.min(h.n, TAPE_MAX_N);
  const need = 8 + hLen + n * (3 * 2 + 4 * 2 + 4 + 5);
  if (raw.byteLength < need) throw new Error("truncated tape");
  const pos = new Float32Array(n * 3);
  const quat = new Float32Array(n * 4);
  const sticks = new Float32Array(n * 4);
  const audio = new Float32Array(n * 5);
  let o = 8 + hLen;
  // read deltas, prefix-sum back with the same Int16 modular wrap, dequantise
  const qpos = new Int16Array(n * 3);
  for (let i = 0; i < n * 3; i++, o += 2) qpos[i] = dv.getInt16(o, true);
  for (let i = 3; i < n * 3; i++) qpos[i] = qpos[i] + qpos[i - 3];
  for (let i = 0; i < n * 3; i++) pos[i] = qpos[i] / POS_Q;
  const qquat = new Int16Array(n * 4);
  for (let i = 0; i < n * 4; i++, o += 2) qquat[i] = dv.getInt16(o, true);
  for (let i = 4; i < n * 4; i++) qquat[i] = qquat[i] + qquat[i - 4];
  for (let i = 0; i < n * 4; i++) quat[i] = qquat[i] / QUAT_Q;
  for (let i = 0; i < n * 4; i++, o += 1) sticks[i] = dv.getInt8(o) / STICK_Q;
  for (let i = 0; i < n * 5; i++, o += 1) audio[i] = raw[o] / 255;
  // renormalise quaternions (quantisation nudges them off unit length)
  for (let i = 0; i < n; i++) {
    const b = i * 4;
    const len = Math.hypot(quat[b], quat[b + 1], quat[b + 2], quat[b + 3]) || 1;
    quat[b] /= len; quat[b + 1] /= len; quat[b + 2] /= len; quat[b + 3] /= len;
  }
  const meta: TapeMeta = {
    fisheye: clamp(Number(h.meta?.fisheye) || 0, 0, 1),
    ground: !!h.meta?.ground,
    groundAlt: clamp(Number(h.meta?.groundAlt) || 12, 2, 60),
    groundDark: clamp(Number(h.meta?.groundDark) || 0.35, 0, 1),
  };
  return { v: 1, hz: h.hz, n, pos, quat, sticks, audio, meta };
}

// --- link transport: base64url over the deflated bytes ---

export function tapeToLink(t: FlightTape): string {
  const bytes = encodeTape(t);
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function linkToTape(b64url: string): FlightTape {
  const b = atob(b64url.replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = new Uint8Array(b.length);
  for (let i = 0; i < b.length; i++) bytes[i] = b.charCodeAt(i);
  return decodeTape(bytes);
}
